import { usePage } from "@inertiajs/react"
import Gleap from "gleap"
import { useEffect } from "react"

import GleapLanguageToggle from "@/components/gleap-language-toggle"
import type { SharedData } from "@/types"

const defaultGleapApiKey = import.meta.env.VITE_GLEAP_API_KEY as
  | string
  | undefined
const agencyGleapApiKey = import.meta.env.VITE_GLEAP_AGENCY_API_KEY as
  | string
  | undefined

declare global {
  interface Window {
    __gleapInitialized?: boolean
    __gleapApiKey?: string
    __gleapLanguage?: string | null
  }
}

interface GleapUser {
  uid?: string
  name?: string
  email?: string
  avatar_url?: string | null
  widget_preferred_language?: string | null
}

interface GleapAccount {
  uid?: string
  name?: string
  account_type?: string
}

interface GleapPageProps extends SharedData {
  current_account?: GleapAccount
  gleap_enabled?: boolean
  gleap_languages?: string[]
  gleap_project?: "agency" | "default" | null
  gleap_screen?: string | null
  business_account?: { uid?: string } & Record<string, unknown>
  marketing_analysis?: { uid?: string } & Record<string, unknown>
  marketing_plan?: { uid?: string } & Record<string, unknown>
  session?: { marketing_analysis_uid?: string } & Record<string, unknown>
  system_task?: { uid?: string } & Record<string, unknown>
}

function gleapApiKeyForProject(project?: GleapPageProps["gleap_project"]) {
  if (project === "agency") {
    return agencyGleapApiKey
  }

  return defaultGleapApiKey
}

function marketingAnalysisUidFromPage(props: GleapPageProps) {
  return (
    props.marketing_analysis?.uid ??
    props.session?.marketing_analysis_uid ??
    null
  )
}

function businessAccountUidFromPage(props: GleapPageProps) {
  return (
    props.business_account?.uid ??
    (props.current_account?.account_type === "business"
      ? props.current_account.uid
      : null) ??
    null
  )
}

function marketingPlanUidFromPage(props: GleapPageProps, url: string) {
  return props.marketing_plan?.uid ?? uidFromPath(url, "marketing_plans")
}

function systemTaskUidFromPage(props: GleapPageProps, url: string) {
  return props.system_task?.uid ?? uidFromPath(url, "to_dos")
}

function uidFromPath(url: string, marker: string) {
  const pathname = url.split("?")[0] ?? ""
  const parts = pathname.split("/").filter(Boolean)
  const markerIndex = parts.indexOf(marker)

  return markerIndex >= 0 ? (parts[markerIndex + 1] ?? null) : null
}

function readCookie(name: string) {
  if (typeof document === "undefined") {
    return null
  }

  const prefix = `${name}=`
  const match = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(prefix))
  if (!match) {
    return null
  }

  return decodeURIComponent(match.slice(prefix.length))
}

function browserLanguage() {
  return navigator.language || "en"
}

function initializeGleap(apiKey: string, language: string | null) {
  if (
    window.__gleapInitialized &&
    window.__gleapApiKey === apiKey &&
    window.__gleapLanguage === language
  ) {
    return
  }

  if (window.__gleapInitialized) {
    Gleap.destroy()
  }

  // Language must be set before initialize; Gleap pins the session (and the
  // AI assistant's replies) to it and offers users no way to change it later.
  Gleap.setLanguage(language ?? browserLanguage())
  Gleap.initialize(apiKey)
  window.__gleapInitialized = true
  window.__gleapApiKey = apiKey
  window.__gleapLanguage = language
}

function syncGleap(
  gleapEnabled: boolean,
  apiKey: string | undefined,
  language: string | null,
) {
  if (!apiKey) {
    return
  }

  if (gleapEnabled) {
    initializeGleap(apiKey, language)
    Gleap.showFeedbackButton(true)
    return
  }

  if (window.__gleapInitialized) {
    Gleap.showFeedbackButton(false)
    Gleap.hide()
  }
}

function syncGleapIdentity(user?: GleapUser, account?: GleapAccount) {
  if (!window.__gleapInitialized) {
    return
  }

  if (!user?.uid) {
    Gleap.clearIdentity()
    return
  }

  Gleap.identify(String(user.uid), {
    name: user.name ?? null,
    email: user.email ?? null,
    avatar: user.avatar_url ?? null,
    companyId: account?.uid ?? null,
    companyName: account?.name ?? null,
  })
}

function syncGleapCustomData(page: { props: GleapPageProps; url: string }) {
  if (!window.__gleapInitialized) {
    return
  }

  const maId = marketingAnalysisUidFromPage(page.props)
  const businessAccountUid = businessAccountUidFromPage(page.props)
  const currentScreen = page.props.gleap_screen ?? null
  const gleapProject = page.props.gleap_project ?? "default"
  const gleapUserToken = readCookie("gleap_user_token")
  const marketingPlanUid = marketingPlanUidFromPage(page.props, page.url)
  const systemTaskUid = systemTaskUidFromPage(page.props, page.url)
  const agencyAccountUid = currentScreen?.startsWith("agency_")
    ? (page.props.current_account?.uid ?? null)
    : null
  const screenContext = {
    current_url: page.url,
    agency_account_uid: agencyAccountUid,
    business_account_uid: businessAccountUid,
    marketing_plan_uid: marketingPlanUid,
    system_task_uid: systemTaskUid,
  }
  const customData = {
    ...screenContext,
    ma_id: maId,
    screen: currentScreen,
    gleap_project: gleapProject,
    gleap_user_token: gleapUserToken,
  }

  Gleap.updateContact({
    customData,
  })

  Gleap.attachCustomData({
    ...customData,
    screen_context: screenContext,
  })
}

export default function GleapController() {
  const page = usePage<GleapPageProps>()
  const {
    auth,
    current_account: currentAccount,
    gleap_enabled: gleapEnabled = false,
    gleap_project: gleapProject,
  } = page.props
  const authUser = auth?.user as GleapUser | undefined
  const gleapApiKey = gleapApiKeyForProject(gleapProject)
  const preferredLanguage = authUser?.widget_preferred_language ?? null

  useEffect(() => {
    syncGleap(gleapEnabled, gleapApiKey, preferredLanguage)
  }, [gleapApiKey, gleapEnabled, preferredLanguage])

  useEffect(() => {
    if (!gleapEnabled) {
      return
    }

    syncGleapIdentity(authUser, currentAccount)
    syncGleapCustomData({
      props: page.props,
      url: page.url,
    })
  }, [authUser, currentAccount, gleapEnabled, page.props, page.url])

  if (!gleapEnabled || !gleapApiKey || !authUser?.uid) {
    return null
  }

  return (
    <GleapLanguageToggle
      languages={page.props.gleap_languages ?? []}
      preferredLanguage={preferredLanguage}
    />
  )
}
